// React
import { memo, useCallback, useEffect, useState } from "react";

// Servisni sloj
import { changeUserPasswordAPI } from "services/api/users/api";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { useHandleTextFieldChange } from "services/forms/hooks";

// UI
import { Button } from "ui/components/Button/Button";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { PasswordField } from "ui/components/TextField/PasswordField";

// Tipovi i konstante
import { UserPasswordEditCardProps, UserPasswordFormValues } from "./types";
import { USER_PASSWORD_FORM_SCHEMA } from "./validation";

const INITIAL_VALUES: UserPasswordFormValues = {
  password: null,
  confirmPassword: null,
};

/**
 * Postavljanje lozinke korisniku.
 *
 * Stara se ne traži jer ovo radi naše osoblje kad korisnik ostane bez pristupa. Zapis o
 * promjeni ide u povijest aktivnosti klijenta; sama lozinka se nigdje ne bilježi.
 */
const UserPasswordEditCard: React.FC<UserPasswordEditCardProps> = (
  props: UserPasswordEditCardProps
) => {
  const { show, userId, userName, onSuccess, onClose } = props;

  const [formValues, setFormValues] = useState<UserPasswordFormValues>(INITIAL_VALUES);
  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Modal ostaje montiran, pa se upisana lozinka ne smije prenijeti na sljedeće otvaranje.
  useEffect(() => {
    if (!show) return;

    setFormValues(INITIAL_VALUES);
    setErrors({});
  }, [show, userId]);

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);

  const handleSave = useCallback(async () => {
    if (!validateYup(formValues, USER_PASSWORD_FORM_SCHEMA, setErrors)) return;

    setIsSaving(true);

    try {
      await changeUserPasswordAPI(userId, formValues.password!);
      onSuccess();
    } catch {
      // greška se prikazuje kroz snackbar
    } finally {
      setIsSaving(false);
    }
  }, [formValues, userId, onSuccess]);

  return (
    <Modal show={show} onHide={onClose} size="small" position="centered">
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={`Promjena lozinke: ${userName}`}
        onHide={onClose}
      />

      <ModalBody fixedFooterSpacing={false}>
        <div className="flex flex-col gap-4 p-4">
          <p className="text-sm text-slate-500">
            Korisnik se nakon spremanja prijavljuje novom lozinkom. Stara prestaje vrijediti
            odmah.
          </p>

          <div>
            <PasswordField
              name="password"
              label="Nova lozinka"
              placeholder="Unesite novu lozinku"
              value={formValues.password}
              onChange={handleTextFieldChange}
              error={errors.password}
              required
              width="full"
            />
          </div>

          <div>
            <PasswordField
              name="confirmPassword"
              label="Ponovite lozinku"
              placeholder="Ponovite novu lozinku"
              value={formValues.confirmPassword}
              onChange={handleTextFieldChange}
              error={errors.confirmPassword}
              required
              width="full"
            />
          </div>
        </div>
      </ModalBody>

      <div className="flex justify-end gap-3 px-4 py-3 border-t border-slate-200 bg-slate-50">
        <Button variant="outlined" children="Odustani" onClick={onClose} />

        <Button
          variant="primary"
          children="Spremi lozinku"
          onClick={handleSave}
          disabled={isSaving}
        />
      </div>
    </Modal>
  );
};

export default memo(UserPasswordEditCard);
